import type { ServerSortColumn } from "components/Table/Table";
import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useEffect, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import { useGetFilesQuery } from "services/file-api";
import type {
  ServerSortableFilesTableDataType,
  FileType,
} from "types/DataTypes";
import { useConstants, useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM, useUrlStore } from "utils/useUrlStore";

import FilePageContainer from "./FilePageContainer";

/**
 * convert Table component sort columns to backend order_by format for files
 * send frontend column names directly - backend handles the conversion
 */
const convertSortToOrderBy = (
  sortColumns: readonly { columnKey: string; direction: "ASC" | "DESC" }[],
): string | null => {
  if (sortColumns.length === 0) {
    return null;
  }

  const { columnKey, direction } = sortColumns[0];
  // Send frontend column name directly - backend converts using its COLUMN_MAPPING
  return direction === "DESC" ? `-${columnKey}` : columnKey;
};

const FILE_COLUMNS = [
  { key: "id", name: "File #", ...commonColumnProps },
  { key: "due_date", name: "Due Date", ...commonColumnProps },
  { key: "datetime_closed", name: "Date Closed", ...commonColumnProps },
  { key: "is_rush", name: "Rush", ...commonColumnProps },
  { key: "client__name", name: "Client", ...commonColumnProps },
  { key: "employer__name", name: "Employer", ...commonColumnProps },
  { key: "status", name: "Status", ...commonColumnProps },
  { key: "services__type", name: "Services", ...commonColumnProps },
  { key: "claimants__first_name", name: "Claimant", ...commonColumnProps },
  { key: "claimants__birth_date", name: "Date of Birth", ...commonColumnProps },
  { key: "dates_of_injury", name: "Date of Injury", ...commonColumnProps },
];

const FILTER_NAME_MAP = {
  service_type: "service",
  file_status: "status",
};

const FileTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const { getFilters } = useUrlStore();
  const filters = getFilters();
  const [page, setPage] = useState(1);
  const [sortColumns, setSortColumns] = useState<readonly ServerSortColumn[]>(
    [],
  );
  const { constants } = useConstants();

  const transformedFilters = Object.entries(filters).reduce(
    (acc, [key, value]) => {
      const backendKey = FILTER_NAME_MAP[key] || key;
      acc[backendKey] = value;
      return acc;
    },
    {},
  );

  // get system user ID from constants to exclude from regular files
  const systemUserId = constants?.SYSTEM_USER?.id || 0;

  // convert sort columns to backend format
  const sortBy = convertSortToOrderBy(sortColumns);

  const {
    data: files = { results: [], count: 0 },
    isLoading,
    isFetching,
  } = useGetFilesQuery(
    {
      searchTerm: query.get(GLOBAL_SEARCH_PARAM),
      filters: {
        ...transformedFilters,
        limit: PAGE_SIZE,
        offset: OFFSET(page),
      },
      sort_by: sortBy || "-id", // default to descending ID sort
      excludeSystemUserId: systemUserId,
    },
    {
      skip: !systemUserId,
    },
  );

  const handleCellClick = (
    args: CellClickArgs<ServerSortableFilesTableDataType, unknown>,
  ) => {
    navigate(`${location.pathname}/${args.row.id}/edit`);
  };

  const handleSortChange = (newSortColumns: readonly ServerSortColumn[]) => {
    setSortColumns(newSortColumns);
    // reset to first page when sorting changes
    setPage(1);
  };

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  // filters live in the URL, so changing one leaves `page` where it was --
  // applying a filter from page 2+ would otherwise request an offset past the
  // end of the narrowed result set and show an empty table.
  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  const rows =
    files.results?.map((file: FileType) => ({
      id: file.id,
      due_date: file.due_date,
      datetime_closed: file.datetime_closed,
      is_rush_verbose: file.is_rush_verbose, // for display
      is_rush: file.is_rush_verbose, // for sorting (maps to backend boolean field)
      status: file.status_verbose,
      date_of_birth: file.claimants[0]?.birth_date,
      dates_of_injury: file.dates_of_injury?.[0]?.date,
      // original keys for backward compatibility
      client_name: file.client?.name,
      employer_name: file.employer?.name,
      services: [...new Set(file.services.map((service) => service.type))].join(
        ", ",
      ),
      claimant_name:
        file.claimants?.length > 0
          ? `${file.claimants[0]?.first_name} ${file.claimants[0]?.last_name}`
          : "",
      // backend field names for server-side sorting
      client__name: file.client?.name,
      employer__name: file.employer?.name,
      services__type: [
        ...new Set(file.services.map((service) => service.type)),
      ].join(", "),
      claimants__first_name:
        file.claimants?.length > 0
          ? `${file.claimants[0]?.first_name} ${file.claimants[0]?.last_name}`
          : "",
      claimants__birth_date: file.claimants[0]?.birth_date, // for sorting
    })) || [];

  return (
    <FilePageContainer>
      <Table
        columns={FILE_COLUMNS}
        rows={rows}
        rowKeyGetter={(row: ServerSortableFilesTableDataType) => row.id}
        onCellClick={handleCellClick}
        isLoading={isFetching}
        fullHeight
        data-testid="files-table"
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: files.count,
          onPageChange: (pageIndex) => setPage(pageIndex),
          isLoading: isFetching,
        }}
        useServerSorting={true}
        serverSorting={{
          sortColumns,
          onSortChange: handleSortChange,
        }}
      />
    </FilePageContainer>
  );
};

export default FileTable;
